import { isToolPart, toolNameOf, WEB_FETCH_TOOL_NAME } from "@esi/shared";
import { safeHref } from "./tool-approvals.ts";

/**
 * Web fetch on the client (openspec change `agent-web-fetch`): the URL a `web_fetch` call asked
 * for and the page it read, as plain text and, at most, a safe link.
 */

/** Whether a tool part is a `web_fetch` call. */
export const isWebFetchPart = (part: unknown): boolean =>
  isToolPart(part) &&
  toolNameOf(part as { type: string; toolName?: unknown }) === WEB_FETCH_TOOL_NAME;

/** The URL a `web_fetch` call asked for, as the fetcher sends it (or would), else null. */
export function fetchUrlOf(input: unknown): string | null {
  const url = (input as { url?: unknown } | null)?.url;
  if (typeof url !== "string" || url === "") {
    return null;
  }
  // As it goes on the wire (percent-encoded): a character rendered as nothing still shows.
  try {
    return new URL(url.trim()).href;
  } catch {
    return url;
  }
}

/**
 * A URL split for display: the scheme and `//`, the host (emphasised), and the rest (path,
 * query, fragment) exactly as written. A string that does not parse is all `rest`.
 */
export function urlParts(url: string): { scheme: string; host: string; rest: string } {
  const match = /^(https?:\/\/)([^/?#]*)(.*)$/is.exec(url);
  return match
    ? { scheme: match[1] ?? "", host: match[2] ?? "", rest: match[3] ?? "" }
    : { scheme: "", host: "", rest: url };
}

/** The page a successful `web_fetch` read, as the activity row shows it. */
export interface FetchedPage {
  /** Its `W#` handle, when the server minted one. */
  source: string | null;
  title: string | null;
  /** The final URL (after redirects), when it is a safe link. */
  href: string | null;
  truncated: boolean;
}

/** The page of a successful `web_fetch` output (`{ external: true, results: [page] }`). */
export function fetchedPageOf(output: unknown): FetchedPage | null {
  if (
    typeof output !== "object" ||
    output === null ||
    (output as { external?: unknown }).external !== true
  ) {
    return null;
  }
  const [page] = ((output as { results?: unknown }).results as unknown[] | undefined) ?? [];
  if (typeof page !== "object" || page === null) {
    return null;
  }
  const o = page as Record<string, unknown>;
  const title = typeof o.title === "string" && o.title.trim() !== "" ? o.title.trim() : null;
  const source =
    typeof o.source === "string" && /^W\d{1,9}$/i.test(o.source) ? o.source.toUpperCase() : null;
  return { source, title, href: safeHref(o.url), truncated: o.truncated === true };
}
